import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import GuestPortal from "@/pages/GuestPortal";

import CustomerDashboard from "@/pages/customer/CustomerDashboard";
import PlotsList from "@/pages/customer/PlotsList";
import PlotDetail from "@/pages/customer/PlotDetail";
import DocumentsPage from "@/pages/customer/DocumentsPage";

import FieldDashboard from "@/pages/field/FieldDashboard";
import LogActivityPage from "@/pages/field/LogActivityPage";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import ProjectsPage from "@/pages/admin/ProjectsPage";
import AdminPlotsPage from "@/pages/admin/AdminPlotsPage";
import AdminPaymentsPage from "@/pages/admin/AdminPaymentsPage";
import FieldDefinitionsPage from "@/pages/admin/FieldDefinitionsPage";
import MaintenancePage from "@/pages/admin/MaintenancePage";
import AdminDocumentsPage from "@/pages/admin/AdminDocumentsPage";
import UsersPage from "@/pages/admin/UsersPage";

import BookingsPage from "@/pages/BookingsPage";
import YieldsPage from "@/pages/YieldsPage";
import RecurringPage from "@/pages/admin/RecurringPage";
import EnquiriesPage from "@/pages/admin/EnquiriesPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import RateRulesPage from "@/pages/admin/RateRulesPage";

const ADMIN_ROLES = ["super_admin", "property_manager"];

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <div className="p-10 text-muted-foreground">Loading…</div>;
  if (!user) return <Landing />;
  return <Navigate to="/app" replace />;
}

function AppHome() {
  const { user } = useAuth();
  if (user?.role === "customer") return <CustomerDashboard />;
  if (user?.role === "field_worker") return <FieldDashboard />;
  return <AdminDashboard />;
}

function AppPlots() {
  const { user } = useAuth();
  if (ADMIN_ROLES.includes(user?.role)) return <AdminPlotsPage />;
  return <PlotsList />;
}

function AppDocuments() {
  const { user } = useAuth();
  if (ADMIN_ROLES.includes(user?.role)) return <AdminDocumentsPage />;
  return <DocumentsPage />;
}

function ShellRoutes() {
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/guest" element={<GuestPortal />} />
          <Route path="/app" element={<ProtectedRoute><ShellRoutes /></ProtectedRoute>}>
            <Route index element={<AppHome />} />
            <Route path="plots" element={<AppPlots />} />
            <Route path="plots/:id" element={<PlotDetail />} />
            <Route path="documents" element={<AppDocuments />} />
            <Route path="bookings" element={<BookingsPage />} />
            <Route path="yields" element={<YieldsPage />} />
            <Route path="recurring" element={<ProtectedRoute roles={ADMIN_ROLES}><RecurringPage /></ProtectedRoute>} />
            <Route path="enquiries" element={<ProtectedRoute roles={ADMIN_ROLES}><EnquiriesPage /></ProtectedRoute>} />
            <Route path="settings" element={<ProtectedRoute roles={["super_admin"]}><SettingsPage /></ProtectedRoute>} />
            <Route path="rate-cards" element={<ProtectedRoute roles={ADMIN_ROLES}><RateRulesPage /></ProtectedRoute>} />
            <Route path="payments" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminPaymentsPage /></ProtectedRoute>} />
            <Route path="field-definitions" element={<ProtectedRoute roles={["super_admin"]}><FieldDefinitionsPage /></ProtectedRoute>} />
            <Route path="log-activity" element={<ProtectedRoute roles={["field_worker", "super_admin", "property_manager"]}><LogActivityPage /></ProtectedRoute>} />
            <Route path="projects" element={<ProtectedRoute roles={ADMIN_ROLES}><ProjectsPage /></ProtectedRoute>} />
            <Route path="maintenance" element={<ProtectedRoute roles={ADMIN_ROLES}><MaintenancePage /></ProtectedRoute>} />
            <Route path="users" element={<ProtectedRoute roles={["super_admin"]}><UsersPage /></ProtectedRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
